import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp } from '@/utils/icons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import type { DeploymentStats } from '@/types/metrics';

interface EOLAlertProps {
  eolHardware: DeploymentStats['eolHardware'];
}

export function EOLAlert({ eolHardware }: EOLAlertProps) {
  const [expanded, setExpanded] = useState(true);

  return (
    <Card className="dls-border-status-warning-tertiary dls-bg-status-warning-quarternary/50 dls-flex dls-flex-col dls-h-full">
      <CardHeader className="dls-pb-2">
        <CardTitle className="dls-flex dls-items-center dls-justify-between dls-text-base">
          <div className="dls-flex dls-items-center dls-gap-2 dls-text-status-warning">
            <AlertTriangle className="dls-h-4.5 dls-w-4.5" />
            <span>
              {eolHardware.totalEol} EOL Device{eolHardware.totalEol !== 1 ? 's' : ''} in Inventory
            </span>
            <InfoTooltip text="End-of-Life hardware that is no longer manufactured or supported. Assigned EOL devices should be scheduled for hardware upgrade. Unassigned EOL devices are not suitable for RMA replacements." />
          </div>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="dls-text-muted-foreground hover:dls-text-foreground"
          >
            {expanded ? <ChevronUp className="dls-h-4 dls-w-4" /> : <ChevronDown className="dls-h-4 dls-w-4" />}
          </button>
        </CardTitle>
      </CardHeader>
      {expanded && (
        <CardContent className="dls-pt-0 dls-flex dls-flex-col dls-flex-1 dls-gap-3">
          <div className="dls-grid dls-grid-cols-2 dls-gap-3 dls-text-sm">
            <div className="dls-rounded-md dls-bg-status-warning-quarternary dls-p-2 dls-border dls-border-status-warning-tertiary dls-flex dls-items-baseline dls-gap-1.5">
              <span className="dls-text-lg dls-font-semibold dls-text-status-warning">
                {eolHardware.assignedEol}
              </span>
              <span className="dls-text-xs dls-text-muted-foreground">Assigned (needs replacement)</span>
            </div>
            <div className="dls-rounded-md dls-bg-status-info/10 dls-p-2 dls-border dls-border-status-info/30 dls-flex dls-items-baseline dls-gap-1.5">
              <span className="dls-text-lg dls-font-semibold dls-text-status-info">
                {eolHardware.unassignedEol}
              </span>
              <span className="dls-text-xs dls-text-muted-foreground">Unassigned (not for RMA)</span>
            </div>
          </div>

          {eolHardware.items.length > 0 && (
            <div className="dls-max-h-48 dls-overflow-y-auto">
              <table className="dls-w-full dls-text-xs">
                <thead>
                  <tr className="dls-text-muted-foreground dls-border-b">
                    <th className="dls-text-left dls-py-1 dls-font-medium">S/N</th>
                    <th className="dls-text-left dls-py-1 dls-font-medium">Vendor</th>
                    <th className="dls-text-left dls-py-1 dls-font-medium">Model</th>
                    <th className="dls-text-left dls-py-1 dls-font-medium">Node Name</th>
                  </tr>
                </thead>
                <tbody>
                  {eolHardware.items.map((item) => (
                    <tr
                      key={item.hardwareSerialNumber}
                      className="dls-border-b dls-border-status-warning-tertiary last:dls-border-0"
                    >
                      <td className="dls-py-1 dls-font-mono">{item.hardwareSerialNumber}</td>
                      <td className="dls-py-1">{item.productVendor ?? 'Unknown'}</td>
                      <td className="dls-py-1">{item.productName ?? 'Unknown'}</td>
                      <td className="dls-py-1">
                        {item.isAssigned ? (
                          <span className="dls-text-status-warning">{item.nodeName ?? 'Assigned'}</span>
                        ) : (
                          <span className="dls-text-muted-foreground">Unassigned</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="dls-flex dls-items-center dls-gap-2 dls-rounded-md dls-bg-status-warning-quarternary dls-p-2 dls-border dls-border-status-warning-tertiary dls-text-xs dls-text-status-warning dls-mt-auto dls-flex-shrink-0">
            <AlertTriangle className="dls-h-3.5 dls-w-3.5 dls-flex-shrink-0" />
            {eolHardware.unassignedEol > 0 ? (
              <span>
                Contact Neeve Support (
                <a href="mailto:support@neeve.ai" className="dls-underline dls-font-medium">
                  support@neeve.ai
                </a>
                ) or your Account Executive to have unassigned EOL serial numbers removed from your
                organization.
              </span>
            ) : (
              <span>
                All EOL devices are currently assigned. Plan hardware upgrades to replace these
                units.
              </span>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
